import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { authAPI } from '../api';

const AuthContext = createContext(null);

const TOKEN_KEY = 'bkw_token';
const USER_KEY = 'bkw_user';

function getStorage() {
  return sessionStorage;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storage = getStorage();
    const token = storage.getItem(TOKEN_KEY);
    const savedUser = storage.getItem(USER_KEY);
    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch {
        storage.removeItem(TOKEN_KEY);
        storage.removeItem(USER_KEY);
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (email, password) => {
    const normalizedEmail = (email || '').trim().toLowerCase();
    const res = await authAPI.login({ email: normalizedEmail, password });
    const storage = getStorage();
    storage.setItem(TOKEN_KEY, res.data.token);
    storage.setItem(USER_KEY, JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data;
  }, []);

  const register = useCallback(async (data) => {
    const normalizedData = { ...data, email: (data.email || '').trim().toLowerCase() };
    const res = await authAPI.register(normalizedData);
    const storage = getStorage();
    storage.setItem(TOKEN_KEY, res.data.token);
    storage.setItem(USER_KEY, JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data;
  }, []);

  const logout = useCallback(() => {
    const storage = getStorage();
    storage.removeItem(TOKEN_KEY);
    storage.removeItem(USER_KEY);
    storage.removeItem('bkw_acting_tenant_id');
    setUser(null);
  }, []);

  // Bootstrap session from a token returned by SSO callback
  const loginFromToken = useCallback(async (token) => {
    const storage = getStorage();
    storage.setItem(TOKEN_KEY, token);
    const res = await authAPI.me();
    storage.setItem(USER_KEY, JSON.stringify(res.data));
    setUser(res.data);
    return res.data;
  }, []);

  // Group auth actions so the value-memo dependency list stays compact (sonarjs/cognitive-complexity).
  const actions = useMemo(() => ({ login, register, logout, loginFromToken }), [login, register, logout, loginFromToken]);

  const value = useMemo(
    () => ({ user, loading, ...actions }),
    [user, loading, actions]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
